import type { OpeningId } from "../lessons/ids.ts";
import type { LessonCourse } from "../lessons/schema.ts";

/**
 * Catalog facts about each course, available without building its graph.
 * A test checks that they match the built course.
 */
export type CourseManifestEntry = {
  openingId: OpeningId;
  slug: string;
  title: string;
  catalogEco: string;
  /** The catalog card's one-line pitch. */
  tagline: string;
  /** What the course trains, in a few words ("Pawn tension and positional planning"). */
  focus: string;
  /** White's first move: the student's in a White course, the one faced in a Black course. */
  firstMove: string;
  level: LessonCourse["level"];
  reviewStatus: NonNullable<LessonCourse["reviewStatus"]>;
};

export const COURSE_MANIFEST: CourseManifestEntry[] = [
  {
    openingId: "italian",
    slug: "italian",
    title: "Italian Game",
    catalogEco: "C50",
    tagline:
      "Build a pawn center, develop toward the king, and learn when to open the game. A clear starting point for your first White repertoire.",
    focus: "Prepare d4 behind c3, d3 and Re1",
    firstMove: "e4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "quietTwoKnights",
    slug: "quiet-two-knights",
    title: "Quiet Two Knights",
    catalogEco: "C55",
    tagline:
      "Against 3.Bc4 …Nf6, play 4.d3. Castle, meet …Na5 with Bb3, and bring the knight through d2 and f1 to g3.",
    focus: "d3 holds e4, then Bb3 and Nbd2–f1–g3",
    firstMove: "e4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "queensGambit",
    slug: "queens-gambit",
    title: "Queen's Gambit Declined",
    catalogEco: "D30",
    tagline:
      "Challenge Black’s central pawn with c4, then use space and coordinated pieces to build pressure. Learn how pawn exchanges change your plan.",
    focus: "Keeping or releasing the tension on d5",
    firstMove: "d4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "ruyLopez",
    slug: "ruy-lopez",
    title: "Ruy Lopez",
    catalogEco: "C84",
    tagline:
      "Pressure the knight that defends e5, preserve your bishop, and prepare a strong center. A patient opening that rewards careful piece improvement.",
    focus: "Keep the b5-bishop, then c3, h3 and d4",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "english",
    slug: "english",
    title: "English Opening",
    catalogEco: "A29",
    tagline:
      "Control the center from the flank and develop a bishop on the long diagonal. Keep your central pawn choices flexible as Black reveals a setup.",
    focus: "d3, d4 or e4, chosen from Black’s setup",
    firstMove: "c4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "london",
    slug: "london",
    title: "London System",
    catalogEco: "D02",
    tagline:
      "Develop your queen’s bishop before closing its diagonal, then build a supported center. Learn a familiar setup while adapting to Black’s pawn breaks.",
    focus: "Keeping the f4-bishop, then the e4 break",
    firstMove: "d4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "alapin",
    slug: "alapin-sicilian",
    title: "Alapin Sicilian (2.c3)",
    catalogEco: "B22",
    tagline:
      "Meet the Sicilian with c3 and prepare a supported d4 advance. Learn how an ambitious pawn center holds up against Black’s immediate counterplay.",
    focus: "exd5 and d4, then an isolated d-pawn",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "vienna",
    slug: "vienna-game",
    title: "Vienna Game",
    catalogEco: "C29",
    tagline:
      "Meet 1…e5 with Nc3 and push f4: the Vienna Gambit. Learn to gain space on e5, cover the weakened diagonal and fight Black’s strong knight on e4.",
    focus: "f4, the e1–h4 diagonal and Black’s e4-knight",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "scotch",
    slug: "scotch-game",
    title: "Scotch Game",
    catalogEco: "C45",
    tagline:
      "Meet 1…e5 2.Nf3 Nc6 with 3.d4 and open the center at once. Learn the Mieses Variation: push e5, defend it, and handle the pin on c4.",
    focus: "Nxd4, the e5 push and the pin on c4",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "fourKnights",
    slug: "four-knights",
    title: "Four Knights Game",
    catalogEco: "C49",
    tagline:
      "Meet 1…e5 2.Nf3 Nc6 with 3.Nc3 and develop calmly. Learn the Spanish Four Knights: pin with Bb5 and Bg5, then meet the Metger Unpin with d4, d5 and c4.",
    focus: "The Bb5 and Bg5 pins, then d4 and d5",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "reti",
    slug: "reti-opening",
    title: "Réti Opening",
    catalogEco: "A09",
    tagline:
      "Develop with 1.Nf3, attack d5 from the side with 2.c4 and fianchetto both bishops. Learn to meet …d4 with exd4, Re1 and the Ba3 bishop trade.",
    focus: "Meeting …d4 with exd4 and the Ba3 trade",
    firstMove: "Nf3",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "openSicilian",
    slug: "open-sicilian",
    title: "Open Sicilian",
    catalogEco: "B92",
    tagline:
      "Meet 1…c5 with 2.Nf3 and 3.d4 and open the center. Against the Najdorf, play the quiet 6.Be2: fight for d5, stop …b5 and press the d6 pawn.",
    focus: "The hole on d5, and a4 against …b5",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "kingsGambit",
    slug: "kings-gambit",
    title: "King's Gambit",
    catalogEco: "C36",
    tagline:
      "Offer the f-pawn with 2.f4, develop fast and win the pawn back with Bxf4. Learn the Modern Defense, the main declined lines and the 3.fxe5?? Qh4+ trap.",
    focus: "Bxf4 to regain the pawn, then pressure on f7",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "pircModern",
    slug: "pirc-modern",
    title: "Pirc and Modern",
    catalogEco: "B08",
    tagline:
      "Meet 1…d6 and 1…g6 with a classical center: d4, Nc3, Nf3 and Be2. Castle short, stop …b5, and press the d6 pawn.",
    focus: "a4 against …b5, and pressure on d6",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "frenchAdvance",
    slug: "french-advance",
    title: "Advance French",
    catalogEco: "C02",
    tagline:
      "Meet the French with 3.e5: hold the d4–e5 chain, gain queenside space with a3 and b4, and take the open c-file. Learn the Euwe, Wade and Lputian lines and the 2…c5 Franco-Sicilian.",
    focus: "Holding d4, the chain’s base, then the c-file",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "scandinavian",
    slug: "scandinavian-main-line",
    title: "Scandinavian Main Line",
    catalogEco: "B01",
    tagline:
      "Meet 1…d5 with 2.exd5 and 3.Nc3: gain time against Black’s queen, take the center with d4 and chase the light-squared bishop with the h- and g-pawns. Learn the queen retreats and the Modern Variation.",
    focus: "Chasing the queen, then the g4-bishop",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "caroAdvance",
    slug: "caro-kann-advance",
    title: "Advance Caro-Kann",
    catalogEco: "B12",
    tagline:
      "Meet the Caro-Kann with 3.e5 and the Short System: develop calmly, recapture on d4 with the knight and strike at d5 with c4. Learn 3…c5, 6…Qb6 and the 7…Bg6 h4 trap.",
    focus: "Nxd4, then c4 against d5",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "caroKann",
    slug: "caro-kann",
    title: "Caro-Kann Defense",
    catalogEco: "B18",
    tagline:
      "Against 1.e4, prepare a supported central challenge with …c6 and …d5. Learn a sturdy defense that still needs active bishops and timely pawn breaks.",
    focus: "The f5-bishop’s retreats, then …c5 or …e5",
    firstMove: "e4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "qgdBlack",
    slug: "qgd-black",
    title: "Queen’s Gambit Declined",
    catalogEco: "D30",
    tagline:
      "Against 1.d4, hold the center with …d5 and …e6, then develop safely. Learn how well-chosen exchanges and pawn breaks turn solidity into activity.",
    focus: "Trades that prepare the freeing …e5",
    firstMove: "d4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "symmetricalEnglish",
    slug: "symmetrical-english",
    title: "Symmetrical English",
    catalogEco: "A30",
    tagline:
      "Against 1.c4, contest the center with …c5 and a kingside fianchetto. Learn when matching White is useful and when the position demands your own plan.",
    focus: "After d4: the c-file and …b5",
    firstMove: "c4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "openGame",
    slug: "open-game",
    title: "Open Game (1…e5)",
    catalogEco: "C50",
    tagline:
      "Against 1.e4, take your share of the center with …e5 and develop actively. Learn classical defense against the Italian, Spanish and early central attacks.",
    focus: "Holding e5, then the freeing …d5",
    firstMove: "e4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "sicilianNajdorf",
    slug: "sicilian-najdorf",
    title: "Sicilian Najdorf",
    catalogEco: "B92",
    tagline:
      "Against 1.e4, create an unbalanced center and counterplay on the c-file. Learn the Najdorf’s queenside expansion and the careful defense of key central squares.",
    focus: "Guarding d5 after …e5, and the …b5 break",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "kingsIndian",
    slug: "kings-indian",
    title: "King’s Indian Defense",
    catalogEco: "E97",
    tagline:
      "Against 1.d4, develop behind a fianchetto and challenge White’s larger center later. Learn when a central break leads to a genuine kingside attack.",
    focus: "After d5: …f5 against White’s queenside race",
    firstMove: "d4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "frenchDefense",
    slug: "french-defense",
    title: "French Defense",
    catalogEco: "C11",
    tagline:
      "Against 1.e4, build a solid …e6 and …d5 chain, then attack White’s pawns at their base. Learn the pawn levers that make a closed center come alive.",
    focus: "…c5 against d4, then a race on opposite wings",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "kingsEnglish",
    slug: "kings-english",
    title: "King’s English (1.c4 e5)",
    catalogEco: "A22",
    tagline:
      "Against 1.c4, take the center with …e5 and strike with …d5. Learn the Reversed Sicilian: active pieces in the center against White’s queenside pawn storm.",
    focus: "…a5 and …Nd4 against b4 and b5",
    firstMove: "c4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "petroffDefense",
    slug: "petroff-defense",
    title: "Petroff Defense",
    catalogEco: "C42",
    tagline:
      "Against 1.e4 e5 2.Nf3, counterattack with …Nf6. Learn a solid, symmetrical defense where exact move order matters more than memorized theory.",
    focus: "…d6 before …Nxe4, then the e4-knight",
    firstMove: "e4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "philidorDefense",
    slug: "philidor-defense",
    title: "Philidor Defense",
    catalogEco: "C41",
    tagline:
      "Against 1.e4 e5 2.Nf3, defend with …d6 and build a compact center. Learn when to hold e5 and when to release it with …exd4 and a knight on c5.",
    focus: "…c6 and …a5 before releasing e5 with …exd4",
    firstMove: "e4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "slavDefense",
    slug: "slav-defense",
    title: "Slav Defense",
    catalogEco: "D18",
    tagline:
      "Against 1.d4 and 2.c4, support d5 with …c6 and keep your light-squared bishop free. Learn when to take on c4 and how to meet White’s e4 and e5.",
    focus: "…Bf5 before …e6, and …Nd5 against e5",
    firstMove: "d4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "nimzoIndian",
    slug: "nimzo-indian",
    title: "Nimzo-Indian Defense",
    catalogEco: "E59",
    tagline:
      "Against 1.d4 and 2.c4, pin White’s knight with …Bb4 and fight for e4 with pieces. Learn when to give up the bishop pair for doubled c-pawns.",
    focus: "…Bxc3 for doubled pawns, then the …e5 break",
    firstMove: "d4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "scandinavianDefense",
    slug: "scandinavian-defense",
    title: "Scandinavian Defense",
    catalogEco: "B01",
    tagline:
      "Against 1.e4, strike at once with …d5. Recapture with the queen, then develop while White spends moves chasing her.",
    focus: "…Qa5, and a g4-bishop that gets chased",
    firstMove: "e4",
    level: "beginner",
    reviewStatus: "draft",
  },
  {
    openingId: "pircDefense",
    slug: "pirc-defense",
    title: "Pirc Defense",
    catalogEco: "B08",
    tagline:
      "Against 1.e4, keep the center flexible with …d6, …Nf6 and …g6. Fianchetto, castle, and break with …e5.",
    focus: "Castle, then …e5 against d4 and e4",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "qgaBlack",
    slug: "queens-gambit-accepted",
    title: "Queen's Gambit Accepted",
    catalogEco: "D20",
    tagline:
      "Against 1.d4 and 2.c4, take the pawn with …dxc4, then hit back with …c5 and …b5. It is the most common club reply to 2.c4, just ahead of …e6.",
    focus: "Give the pawn back, then …c5 and …b5",
    firstMove: "d4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "twoKnights",
    slug: "two-knights",
    title: "Two Knights Defense",
    catalogEco: "C55",
    tagline:
      "Against 1.e4 e5 2.Nf3 Nc6 3.Bc4, develop with …Nf6. Meet the quiet 4.d3 and know the answer to 4.Ng5.",
    focus: "…d5 and …Na5 against 4.Ng5",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "englishNf6",
    slug: "english-nf6",
    title: "English Nf6",
    catalogEco: "A15",
    tagline: "Against 1.c4, play …Nf6, …g6 and …Bg7. Castle, then strike in the center with …e5.",
    focus: "…Bg7, castle, then …e5 and …a5 against b4",
    firstMove: "c4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "retiBlack",
    slug: "reti-for-black",
    title: "Réti for Black",
    catalogEco: "A13",
    tagline:
      "Against 1.Nf3, answer …d5. Meet c4 and g3 with …e6, …Nf6 and …Be7, then break with …c5 and …d4.",
    focus: "…c5 and …d4 against the g2-bishop",
    firstMove: "Nf3",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "classicalSicilian",
    slug: "classical-sicilian",
    title: "Classical Sicilian",
    catalogEco: "B56",
    tagline:
      "Against 1.e4, play …c5 and …Nc6. After the center opens, …Nf6, …d6 and …e5 build the Boleslavsky structure.",
    focus: "The Boleslavsky …e5, then …a5 and …Nb4",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "kanSicilian",
    slug: "kan-sicilian",
    title: "Kan Sicilian",
    catalogEco: "B42",
    tagline:
      "Against 1.e4, play …c5 and …e6, then …a6. Build a hedgehog with …d6, …g6, …b6 and the bishop on b7.",
    focus: "The hedgehog: …d6, …g6, …b6 and …Bb7",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "berlinDefense",
    slug: "berlin-defense",
    title: "Berlin Defense",
    catalogEco: "C65",
    tagline:
      "Against the Ruy Lopez, answer 3.Bb5 with …Nf6. The lesson is the solid 4.d3 system, and 4.O-O is the Berlin endgame.",
    focus: "…Bc5, then the knight’s route e7 to g6",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "steinitzDefense",
    slug: "steinitz-defense",
    title: "Steinitz Defense",
    catalogEco: "C62",
    tagline:
      "Against the Ruy Lopez, answer 3.Bb5 with …d6. Develop the bishop to d7, castle, and break with …c5.",
    focus: "…d6 and …Bd7, then the …c5 break",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
  {
    openingId: "classicalRuy",
    slug: "classical-ruy",
    title: "Classical Ruy",
    catalogEco: "C64",
    tagline:
      "Against the Ruy Lopez, answer 3.Bb5 with …Bc5. Retreat the bishop to b6 when d4 arrives, then challenge the light-squared bishop with …Na5.",
    focus: "…Bc5 to b6, then …Na5 at the bishop",
    firstMove: "e4",
    level: "intermediate",
    reviewStatus: "draft",
  },
];

export function manifestByOpeningId(id: string): CourseManifestEntry | undefined {
  return COURSE_MANIFEST.find((entry) => entry.openingId === id);
}

export function manifestBySlug(slug: string): CourseManifestEntry | undefined {
  return COURSE_MANIFEST.find((entry) => entry.slug === slug);
}
